import React, { PropsWithChildren, useRef, useCallback, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import classNames from 'classnames';
import { useCPortal } from "@/src/infrastructure/provider";
import { CPortalLayer } from "@/src/infrastructure/provider/portals";
import { NativeProps } from '@/src/components/interfaces';
import { CBox } from '@/src/components/c-box/CBox';
import { CFlex, AlignItems, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CText, CTextSize, CTextWeight } from '@/src/components/c-text/CText';
import { CDivider, CDividerColor } from '@/src/components/c-divider/CDivider';
import { CSpacing } from '@/src/components/interfaces';
import { PresenterStore } from '@/src/infrastructure/presenter/store';

export interface CBottomPanelProps<TInput = void, TOutput = void> extends NativeProps {
  presenter: PresenterStore<TInput, TOutput>;
  header: {
    title: string;
    slot?: React.ReactNode;
  };
  scrollable?: boolean;
  footer?: {
    slot?: React.ReactNode;
  };
}

export function CBottomPanel<TInput = void, TOutput = void>(props: PropsWithChildren<CBottomPanelProps<TInput, TOutput>>): React.JSX.Element | null {
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const dragHeightRef = useRef<number | null>(null);

  const { rendered, visible } = props.presenter.state;

  useEffect(() => {
    if (rendered) {
      setDragHeight(null);
      dragHeightRef.current = null;
    }
  }, [rendered]);

  const handleDragStart = useCallback((clientY: number) => {
    if (!panelRef.current) return;
    setDragging(true);
    startY.current = clientY;
    startHeight.current = panelRef.current.offsetHeight;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    const delta = startY.current - clientY;
    const newHeight = Math.min(window.innerHeight * 0.9, Math.max(60, startHeight.current + delta));
    dragHeightRef.current = newHeight;
    setDragHeight(newHeight);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragging(false);
    const h = dragHeightRef.current;
    if (h !== null && h < window.innerHeight * 0.15) {
      props.presenter.reject();
    } else if (h !== null && h > window.innerHeight * 0.7) {
      setDragHeight(window.innerHeight * 0.9);
      dragHeightRef.current = window.innerHeight * 0.9;
    }
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientY);
  }, [handleDragStart]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  }, [handleDragStart]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => handleDragMove(e.touches[0].clientY);
    const onUp = () => handleDragEnd();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, handleDragMove, handleDragEnd]);

  const portalContainer = useCPortal(CPortalLayer.BottomPanel);
  if (!rendered) return null;

  const panelStyle: React.CSSProperties = dragHeight !== null
    ? { height: `${dragHeight}px`, transition: dragging ? 'none' : 'height 200ms ease-out, transform 300ms ease-out' }
    : {};

  return createPortal(
    <div
      className={classNames('c-bottom-panel__curtain', { 'c-bottom-panel__curtain--visible': visible })}
      onClick={(e) => { e.stopPropagation(); props.presenter.reject(); }}
    >
      <div
        ref={panelRef}
        className={classNames('c-bottom-panel', { 'c-bottom-panel--visible': visible }, props.class)}
        id={props.id}
        data-test-selector={props.testSelector}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="c-bottom-panel__handle"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <div className="c-bottom-panel__handle-bar" />
        </div>

        <div className="c-bottom-panel__grid">
          <CBox class="c-bottom-panel__header c-padding--horizontal--xlg c-padding--vertical--md">
            <CFlex justifyContent={JustifyContent.SpaceBetween} alignItems={AlignItems.Center} fullHeight>
              <CText size={CTextSize.Md} weight={CTextWeight.SemiBold}>{props.header.title}</CText>
              {props.header.slot}
            </CFlex>
          </CBox>

          <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />

          <CBox class={classNames('c-bottom-panel__content c-padding--xlg', { 'c-bottom-panel__content--no-scroll': props.scrollable === false })}>
            {props.children}
          </CBox>

          {props.footer?.slot && (
            <>
              <CDivider color={CDividerColor.Light} marginVertical={CSpacing.None} marginHorizontal={CSpacing.Xlg} />
              <CBox class="c-bottom-panel__footer c-padding--horizontal--xlg c-padding--vertical--md">
                {props.footer.slot}
              </CBox>
            </>
          )}
        </div>
      </div>
    </div>
  , portalContainer ?? document.body);
}
